import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Clock, 
  ChevronDown,
  ArrowRight,
  Search,
  HelpCircle,
  CreditCard,
  Shield,
  Users,
  Settings,
  Zap
} from 'lucide-react';

export const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'getting-started', name: 'Getting Started', icon: Zap },
    { id: 'billing', name: 'Billing & Pricing', icon: CreditCard },
    { id: 'features', name: 'Features', icon: Settings },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'team', name: 'Team Management', icon: Users },
  ];

  const faqs = [
    {
      category: 'getting-started',
      question: "How do I get started with TeamTime?",
      answer: "Getting started is easy! Simply click 'Start Free Trial' to create your organization account. You'll be guided through a quick setup process where you can add your company details, invite team members, and configure your first schedule. The whole process takes less than 5 minutes."
    },
    {
      category: 'getting-started',
      question: "Do I need to install any software?",
      answer: "No installation required! TeamTime is a cloud-based application that works directly in your web browser. It's also a Progressive Web App (PWA), which means your team can add it to their home screen on mobile devices for quick access."
    },
    {
      category: 'getting-started',
      question: "How do I add employees to my organization?",
      answer: "You can add employees in two ways: 1) Send email invitations directly from the dashboard, or 2) Share your unique Organization ID that employees can use during registration. You can manage roles and permissions for each team member from the Settings page."
    },
    {
      category: 'billing',
      question: "Is there really a free tier?",
      answer: "Yes! Our Free tier is completely free forever—no credit card required. It supports up to 5 employees, 2 departments, and 1 location. It's perfect for small teams or for trying out TeamTime before upgrading."
    },
    {
      category: 'billing',
      question: "How does billing work?",
      answer: "All paid plans are billed monthly. You can upgrade, downgrade, or cancel at any time. When you upgrade, you'll be charged immediately for the new plan. When you downgrade, the change takes effect at the start of your next billing cycle."
    },
    {
      category: 'billing',
      question: "What payment methods do you accept?",
      answer: "We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure payment partner, Stripe. For customers in the Caribbean region, we also support local currency payments through PowerTranz."
    },
    {
      category: 'billing',
      question: "Can I get a refund?",
      answer: "We offer a 14-day money-back guarantee on all paid plans. If you're not satisfied within the first 14 days, contact our support team for a full refund. After 14 days, you can cancel anytime but refunds are not provided for partial months."
    },
    {
      category: 'features',
      question: "How does the geo-fencing feature work?",
      answer: "Geo-fencing allows you to set a designated work area for each branch/location. When employees clock in, their GPS location is checked against this boundary. If they're outside the designated area, the entry is automatically flagged for manager review—but still recorded."
    },
    {
      category: 'features',
      question: "Can employees swap shifts with each other?",
      answer: "Yes! With the Pro and Enterprise plans, employees can request shift swaps. The process is simple: an employee requests to swap with a colleague, both parties confirm, and a manager approves. All parties receive notifications throughout the process."
    },
    {
      category: 'features',
      question: "What reports can I generate?",
      answer: "TeamTime offers comprehensive reporting including: Time entries (hours worked, clock-in/out times), Employee directory, Schedule reports, Vacation/leave reports, and Analytics dashboards. All reports can be exported as PDF or CSV files."
    },
    {
      category: 'features',
      question: "How do vacation requests work?",
      answer: "Employees can submit vacation requests through their dashboard, specifying the dates and type of leave. Managers receive instant notifications and can approve or reject requests with a single click. The system automatically prevents scheduling conflicts."
    },
    {
      category: 'security',
      question: "Is my data secure?",
      answer: "Absolutely. We use bank-grade encryption (256-bit SSL) for all data transmission. Your data is stored in secure, redundant data centers with automatic backups. We're committed to protecting your information and comply with GDPR and other privacy regulations."
    },
    {
      category: 'security',
      question: "Who can access my organization's data?",
      answer: "Only authorized users within your organization can access your data. Our role-based access control ensures that employees only see information relevant to their role. Admins have full control over permissions and can audit access at any time."
    },
    {
      category: 'security',
      question: "Do you share data with third parties?",
      answer: "No, we never sell or share your data with third parties for marketing purposes. The only exceptions are our essential service providers (like payment processors) who are bound by strict data protection agreements."
    },
    {
      category: 'team',
      question: "What's the difference between user roles?",
      answer: "TeamTime has four main roles: Admin (full access, billing, settings), Manager (schedules, approvals, reports), Supervisor (team oversight, limited approvals), and Employee (personal time tracking, requests). Each role has specific permissions tailored to their responsibilities."
    },
    {
      category: 'team',
      question: "Can I have multiple locations/branches?",
      answer: "Yes! The number of branches depends on your plan: Free (1 branch), Pro (up to 5 branches), Enterprise (unlimited). Each branch can have its own address, geo-fence settings, and assigned employees."
    },
    {
      category: 'team',
      question: "How do I organize employees into departments?",
      answer: "From the Settings page, you can create departments and assign a manager to each. Then, when adding or editing employees, you can assign them to specific departments. This helps with scheduling, reporting, and organizational structure."
    },
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">TeamTime</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</Link>
              <Link to="/#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</Link>
              <Link to="/faq" className="text-orange-600 font-medium">FAQ</Link>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="hidden sm:inline-flex">Log In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-orange-50/30 to-teal-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Find answers to common questions about TeamTime. Can't find what you're looking for? Contact our support team.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 outline-none text-lg"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <category.icon className="w-4 h-4" />
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No results found</h3>
              <p className="text-slate-600">Try adjusting your search or category filter.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFAQs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all hover:border-slate-300"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-6">
                      <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-slate-600 mb-8">
            Our support team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
              Contact Support
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-2">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">TeamTime</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} TeamTime. All rights reserved.</p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
